// frontend/src/contexts/SocketContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import logger from "../utils/logger";

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [socketError, setSocketError] = useState(null);

  const socketInstanceRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let newSocket = null;

    if (isAuthenticated && token) {
      if (socketInstanceRef.current) {
        logger.debug('SocketContext: Existing socket in ref, disconnecting before new connection attempt.');
        socketInstanceRef.current.disconnect();
        socketInstanceRef.current = null;
      }

      logger.info("SocketContext: Auth token present, attempting to connect socket.");

      const baseSocketUrl = import.meta.env.VITE_WEBSOCKET_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const socketConnectUrl = baseSocketUrl;

      newSocket = io(
        socketConnectUrl,
        {
          path: '/socket.io/',
          auth: { token },
          transports: ['websocket', 'polling'],
          forceNew: true,
          reconnectionAttempts: 3,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000
        }
      );

      // --- ADD THIS CRITICAL DIAGNOSTIC LINE HERE ---
      logger.debug(`SocketContext: Client attempting to connect to resolved URL: ${newSocket.io.uri} with path: ${newSocket.io.opts.path}`);
      // --- END CRITICAL DIAGNOSTIC LINE ---

      socketInstanceRef.current = newSocket;

      newSocket.on('connect', () => {
        if (isMounted) {
          logger.info('Socket.IO: Connected successfully.', { socketId: newSocket.id });
          setSocketError(null);
          setSocket(newSocket);
        } else {
          logger.debug('SocketContext: Connected, but component unmounted. Disconnecting socket.');
          newSocket.disconnect();
        }
      });

      newSocket.on('disconnect', (reason) => {
        if (isMounted) {
          logger.warn('Socket.IO: Disconnected.', { reason });
          setSocket(null);
          setSocketError(null);
        } else {
          logger.debug('SocketContext: Disconnected, and component already unmounted.');
        }
        socketInstanceRef.current = null;
      });

      newSocket.on('connect_error', (err) => {
        if (isMounted) {
          logger.error('Socket.IO: Connection Error.', { message: err.message, data: err.data });
          setSocketError(err.message || 'Failed to connect to real-time service.');
          setSocket(null);
        } else {
          logger.debug('SocketContext: Connection error, but component already unmounted.');
        }
        socketInstanceRef.current = null;
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        logger.info(`Socket.IO: Reconnect attempt ${attemptNumber}...`);
      });

      newSocket.on('reconnect_error', (err) => {
        logger.error('Socket.IO: Reconnect error.', { message: err.message });
      });

      newSocket.on('reconnect_failed', () => {
        logger.error('Socket.IO: Reconnect failed permanently.');
        if (isMounted) setSocketError('Failed to reconnect to real-time service.');
      });

    } else if (!isAuthenticated && socketInstanceRef.current) {
      logger.info("SocketContext: User logged out, disconnecting socket.");
      socketInstanceRef.current.disconnect();
      socketInstanceRef.current = null;
      setSocket(null);
      setSocketError(null);
    }

    return () => {
      isMounted = false;
      if (socketInstanceRef.current) {
        logger.debug("SocketContext: Cleaning up socket instance from ref on effect cleanup.");
        socketInstanceRef.current.disconnect();
        socketInstanceRef.current = null;
      }
      setSocket(null);
      setSocketError(null);
    };
  }, [token, isAuthenticated]);

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  return useContext(SocketContext);
};